import { useState } from 'react';
import { useFormikContext } from 'formik';
import { Download } from 'lucide-react';
import Papa from 'papaparse';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { Button } from '@/components/ui/button';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { toast } from '@/components/ui/toast';

import { useDownloadSampleTemplateMutation } from '@/query/scheduler.query';

import { type BatchCallFormValues, type Recipient } from '@/schemas/batch-call.schema';

// ---------------------------------------------------------------------------
// Validation & mapping
// ---------------------------------------------------------------------------
type CsvRow = Record<string, string>;

const REQUIRED_COLUMNS = ['full_name', 'phone_number', 'metadata'] as const;

const validatePhoneNumber = (phone: string) => phone.replace(/\D/g, '').length >= 10;

const validateCsvHeaders = (rows: CsvRow[]): string | null => {
  if (rows.length === 0) return 'The CSV file is empty.';
  const headers = Object.keys(rows[0]);
  const missing = REQUIRED_COLUMNS.filter(col => !headers.includes(col));
  if (missing.length > 0) {
    return `Missing required column${missing.length > 1 ? 's' : ''}: ${missing.map(c => `"${c}"`).join(', ')}.`;
  }
  return null;
};

const buildRecipients = (rows: CsvRow[]): Recipient[] =>
  rows
    .filter(r => r.full_name && r.phone_number)
    .map(({ full_name, phone_number, metadata }) => {
      let parsedMetadata: Record<string, unknown> = {};
      try {
        parsedMetadata = metadata ? JSON.parse(metadata) : {};
      } catch {
        parsedMetadata = { raw: metadata };
      }
      return {
        name: full_name.trim(),
        phoneNumber: phone_number.trim(),
        metadata: parsedMetadata,
        status: validatePhoneNumber(phone_number) ? 'valid' : ('invalid' as const),
      };
    });

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const UploadRecipientsField = () => {
  const { values, setFieldValue, errors, touched } = useFormikContext<BatchCallFormValues>();
  const [file, setFile] = useState<File | null>(null);

  const { mutate: downloadTemplate, isPending: isDownloading } = useDownloadSampleTemplateMutation({
    onSuccess: () => toast.success('Template downloaded'),
  });

  const handleChange = (selected: File | null) => {
    if (!selected) {
      setFile(null);
      setFieldValue('uploadedFileName', '');
      setFieldValue('recipients', []);
      return;
    }

    Papa.parse<CsvRow>(selected, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data }) => {
        const headerError = validateCsvHeaders(data);
        if (headerError) {
          toast.error(headerError);
          return;
        }
        const recipients = buildRecipients(data);
        setFile(selected);
        setFieldValue('uploadedFileName', selected.name);
        setFieldValue('recipients', recipients);
        toast.success(`${recipients.length} recipient${recipients.length !== 1 ? 's' : ''} loaded`);
      },
      error: err => toast.error(`Failed to parse file: ${err.message}`),
    });
  };

  return (
    <div className="space-y-2">
      <RequiredLabel className="text-sm font-medium text-zinc-950">Upload Recipients</RequiredLabel>
      <div className="space-y-3">
        <FileDropzone
          value={file}
          onChange={handleChange}
          accept=".csv"
          acceptLabel="CSV"
          invalid={!!(touched.recipients && errors.recipients)}
          onError={msg => toast.error(msg)}
        />

        {values.recipients.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {values.recipients.length} recipient{values.recipients.length !== 1 ? 's' : ''} loaded
          </p>
        )}

        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={() => {
            if (!values.agentId) {
              toast.error('Please select an agent before downloading the template');
              return;
            }
            downloadTemplate(values.agentId);
          }}
          disabled={isDownloading}
          className="gap-1 text-xs h-auto p-0 justify-start"
        >
          <Download className="h-3 w-3" />
          {isDownloading ? 'Downloading...' : 'Download sample template'}
        </Button>
      </div>
      <ErrorField name="recipients" />
    </div>
  );
};
